import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CalendarDays, Wallet } from "lucide-react";
import {
  formatarBRL, formatarDataCurta, dataPagamentoDaCompetencia, competenciaDaQuinzena,
  proximaCompetencia,
} from "@shared/equipe-fechamento";

interface Props {
  /** `periodo_inicio` da quinzena (YYYY-MM-DD) — de onde a competência (D11) vem. */
  periodoInicio: string;
  /** `data_pagamento` da quinzena. Null = ainda não definida. */
  dataPagamento: string | null;
  /** Total a sair NESTA quinzena: folha + adicionais + adiantamentos. */
  totalASair: string;
  /** Σ dos fechamentos ABERTOS da competência — o que ainda vai sair no mês. */
  totalFechamentoMes: string;
  /** true = `totalFechamentoMes` é projeção (nenhum fechamento gerado ainda). */
  totalFechamentoMesPrevisto: boolean;
  diaPagamentoMensal: number | null;
  /** Só a quinzena ABERTA pede a data; nas outras o cartão é só leitura. */
  podeDefinirData: boolean;
  onDefinirData: (iso: string) => void;
  salvando?: boolean;
  compacto?: boolean;
}

/**
 * §4.1 — as DUAS datas de pagamento no topo do Financeiro.
 *
 * São dois dinheiros diferentes e em momentos diferentes, e é por isso que os
 * cartões são dois: o da quinzena (diaristas + adiantamento dos mensalistas) e o
 * do fechamento do mês (o resto do salário do mensalista). Juntar os dois num
 * número só foi o que fez a fazenda continuar no caderno.
 *
 * Quinzena antiga não tem `data_pagamento` (a coluna nasceu na migração 027) e
 * mostra "—" no cartão "Paga em": o passado não é reescrito nem preenchido
 * por chute. O cartão "Fecha mês em" não tem esse problema — a competência
 * (D11 v2.7) vem de `periodo_inicio`, que toda quinzena sempre tem.
 */
export function CartoesDeData({
  periodoInicio, dataPagamento, totalASair, totalFechamentoMes, totalFechamentoMesPrevisto, diaPagamentoMensal,
  podeDefinirData, onDefinirData, salvando, compacto,
}: Props) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(dataPagamento ?? "");

  // D11 (v2.7) — competência = mês em que a quinzena COMEÇA, nunca o mês de
  // pagamento. A quinzena 18/07–01/08 é competência 2026-07 mesmo pagando em
  // 01/08 — é por isso que não depende de `dataPagamento` estar definida.
  const competencia = competenciaDaQuinzena({ periodoInicio });
  // O fechamento da competência X é pago no dia_pagamento_mensal do mês
  // SEGUINTE a X — a quinzena 18/07–01/08 (competência 2026-07) fecha em
  // 05/08, não dentro do próprio julho.
  const dataFechamento = dataPagamentoDaCompetencia(proximaCompetencia(competencia), diaPagamentoMensal);

  const pad = compacto ? "p-2.5" : "p-3";
  const titulo = compacto ? "text-[10px]" : "text-xs";
  const valorCls = compacto ? "text-sm" : "text-base";

  return (
    <div className="grid grid-cols-2 gap-2">
      {/* 💵 Paga em — o dinheiro desta quinzena */}
      <div className={`rounded-xl border border-sucesso/30 bg-sucesso-suave ${pad}`}>
        <p className={`flex items-center gap-1 font-medium uppercase text-sucesso ${titulo}`}>
          <Wallet className="h-3 w-3 shrink-0" />Paga em
        </p>
        {editando ? (
          <div className="mt-1 space-y-1.5">
            <input
              type="date"
              value={valor}
              onChange={e => setValor(e.target.value)}
              className="w-full min-w-0 rounded-lg border border-sucesso/30 px-2 py-1 text-sm bg-white"
            />
            <div className="flex gap-1">
              <Button
                size="sm"
                className="h-7 flex-1 text-xs"
                disabled={!valor || salvando}
                onClick={() => { onDefinirData(valor); setEditando(false); }}
              >
                Salvar
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditando(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : !dataPagamento && podeDefinirData ? (
          <button
            type="button"
            onClick={() => setEditando(true)}
            className="mt-1 text-sm font-medium text-sucesso underline underline-offset-2"
          >
            definir data
          </button>
        ) : (
          <>
            <p className={`font-bold text-sucesso ${valorCls}`}>{formatarDataCurta(dataPagamento)}</p>
            <p className="text-xs font-medium text-sucesso">{formatarBRL(totalASair)}</p>
            {podeDefinirData && (
              <button
                type="button"
                onClick={() => { setValor(dataPagamento ?? ""); setEditando(true); }}
                className="text-[10px] text-sucesso underline underline-offset-2"
              >
                alterar
              </button>
            )}
          </>
        )}
      </div>

      {/* 🗓️ Fecha mês em — o que ainda sai no fechamento */}
      <div className={`rounded-xl border border-info/30 bg-info-suave ${pad}`}>
        <p className={`flex items-center gap-1 font-medium uppercase text-info ${titulo}`}>
          <CalendarDays className="h-3 w-3 shrink-0" />Fecha mês em
        </p>
        <p className={`font-bold text-info ${valorCls}`}>{formatarDataCurta(dataFechamento)}</p>
        <p className="text-xs font-medium text-info">
          {formatarBRL(totalFechamentoMes)}
          {totalFechamentoMesPrevisto && (
            <span className="font-normal text-info"> · previsto</span>
          )}
        </p>
        <p className="text-[10px] text-info">competência {competencia}</p>
      </div>
    </div>
  );
}
